import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Inbox, UserCheck, Loader2 } from "lucide-react";

const LEVEL_LABEL = {
  pending_l1: "L1",
  pending_l2: "L2",
  pending_l3: "L3",
};

export default function AdminBeneficiaryApprovals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // { bene, action: "approve" | "reject" }
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const role = (user?.role || "").toLowerCase();
  const isApprover = ["admin", "approver_l1", "approver_l2", "approver_l3", "approver_all"].includes(role);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/beneficiary-approvals");
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load approval queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isApprover) load(); }, [isApprover]);

  const submitAction = async () => {
    if (!selected) return;
    const { bene, action } = selected;
    if (action === "reject" && !note.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    setBusy(true);
    try {
      const path = `/admin/beneficiary-approvals/${bene.id}/${action}`;
      const body = action === "reject" ? { reason: note.trim() } : { note: note.trim() || null };
      const { data } = await api.post(path, body);
      toast.success(data.message || `Beneficiary ${action}d`);
      setSelected(null);
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (!isApprover) {
    return (
      <DashboardLayout>
        <div className="container max-w-2xl mx-auto py-12">
          <h1 className="font-display text-2xl font-bold">Access denied</h1>
          <p className="text-muted-foreground mt-1">You don't have an approver role.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold">Beneficiary approvals</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Customer-scoped beneficiaries where the KYC name didn't auto-match the account holder.
              Approve through L1 → L2 → L3 to release for payouts.
            </p>
          </div>
          <Badge variant="outline" className="text-sm">
            <Inbox className="h-3.5 w-3.5 mr-1" /> {items.length} pending
          </Badge>
        </div>

        <div className="surface-card overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Loading queue…
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <UserCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <div className="font-medium">No beneficiaries pending your review</div>
              <div className="text-xs mt-1">New customer-mode benes with KYC name mismatches will appear here.</div>
            </div>
          ) : (
            <Table data-testid="bene-approval-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Level</TableHead>
                  <TableHead>Customer mobile</TableHead>
                  <TableHead>Beneficiary</TableHead>
                  <TableHead>KYC name → Holder name</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((b) => (
                  <TableRow key={b.id} data-testid={`bene-row-${b.id}`}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {LEVEL_LABEL[b.approval_status] || b.approval_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">+91 {b.customer_mobile}</TableCell>
                    <TableCell>
                      <div className="font-medium text-sm">{b.nickname}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">
                        {b.type === "upi" ? b.upi_id : `${b.account_number?.slice(-4)} · ${b.ifsc}`}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="text-muted-foreground">{b.customer_kyc_name_at_submit || "—"}</div>
                      <div className="font-medium">{b.account_holder_name || "—"}</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(b.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm" variant="outline"
                          className="border-success/40 text-success hover:bg-success/10"
                          onClick={() => { setSelected({ bene: b, action: "approve" }); setNote(""); }}
                          data-testid={`bene-approve-${b.id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="border-destructive/40 text-destructive hover:bg-destructive/10"
                          onClick={() => { setSelected({ bene: b, action: "reject" }); setNote(""); }}
                          data-testid={`bene-reject-${b.id}`}
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selected?.action === "approve" ? "Approve beneficiary" : "Reject beneficiary"}
            </DialogTitle>
            <DialogDescription>
              {selected?.bene && (
                <span className="font-mono text-xs">
                  +91 {selected.bene.customer_mobile} · {selected.bene.nickname} ·{" "}
                  {selected.bene.type === "upi" ? selected.bene.upi_id : selected.bene.account_number?.slice(-4)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {selected?.action === "approve" ? (
              <>
                <div className="text-xs text-muted-foreground bg-muted/40 rounded p-2">
                  Approving from <span className="font-mono">{selected.bene?.approval_status}</span>.
                  On final L3 approval the beneficiary will be registered with the payout provider.
                  No fee refund is issued on reject (per policy).
                </div>
                <div>
                  <Label className="text-xs">Note (optional)</Label>
                  <Textarea
                    value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Manually verified with customer over phone."
                    rows={3} className="mt-1 text-sm"
                    data-testid="bene-approve-note"
                  />
                </div>
              </>
            ) : (
              <div>
                <Label className="text-xs">Rejection reason <span className="text-destructive">*</span></Label>
                <Textarea
                  value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Required — explain why this beneficiary cannot be approved."
                  rows={4} className="mt-1 text-sm"
                  data-testid="bene-reject-reason"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  This will be permanently recorded in the audit trail and visible to the partner.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={submitAction} disabled={busy}
              className={selected?.action === "reject" ? "bg-destructive hover:bg-destructive/90" : ""}
              data-testid="bene-action-confirm"
            >
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {selected?.action === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
